import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Map<HTMLElement, ReturnType<typeof setInterval>>} */
  #countdownTimers = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#clearAllCountdowns();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          trapFocus(details);
          this.#initCountdowns(details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  // ---------------------------------------------------------------------------
  // Countdown helpers
  // ---------------------------------------------------------------------------

  /**
   * Zero-pad a number to two digits.
   * @param {number} n
   * @returns {string}
   */
  #pad(n) {
    return String(n).padStart(2, '0');
  }

  /**
   * Format a millisecond difference into a human-readable countdown string.
   * Returns null when the countdown has expired.
   * @param {number} diff - Remaining milliseconds
   * @returns {string | null}
   */
  #formatCountdown(diff) {
    if (diff <= 0) return null;

    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);

    return d > 0
      ? `${this.#pad(d)}d: ${this.#pad(h)}h: ${this.#pad(m)}m: ${this.#pad(s)}s`
      : `${this.#pad(h)}h: ${this.#pad(m)}m: ${this.#pad(s)}s`;
  }

  /**
   * Start a countdown ticker for a single `.menu-countdown[data-end]` element.
   * Skips elements that already have a running timer tracked by this instance.
   * @param {HTMLElement} el
   */
  #initMenuCountdown(el) {
    // Skip if we already have a live timer for this element
    if (this.#countdownTimers.has(el)) return;

    const endDate = new Date(el.dataset.end).getTime();
    if (isNaN(endDate)) return;

    const hide = () => {
      const li = el.closest('li');
      if (li) {
        li.style.display = 'none';
      } else {
        el.style.display = 'none';
      }
    };

    const tick = () => {
      const diff = endDate - Date.now();

      // if (diff <= 0) {
      //   hide();
      //   return;
      // }

      el.textContent = this.#formatCountdown(diff);
    };

    tick();

    const timer = setInterval(() => {
      const diff = endDate - Date.now();

      if (diff <= 0) {
        clearInterval(timer);
        this.#countdownTimers.delete(el);
      }

      tick();
    }, 1000);

    this.#countdownTimers.set(el, timer);
  }

  /**
   * Find and initialise all `.menu-countdown[data-end]` elements within a
   * given root (defaults to the whole component).
   * @param {Element} [root]
   */
  #initCountdowns(root = this) {
    root.querySelectorAll('.menu-countdown[data-end]').forEach((el) => {
      if (el instanceof HTMLElement) {
        this.#initMenuCountdown(el);
      }
    });
  }

  /**
   * Clear every active countdown timer managed by this component.
   */
  #clearAllCountdowns() {
    this.#countdownTimers.forEach((timer) => clearInterval(timer));
    this.#countdownTimers.clear();
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}